/*
 * Variant media
 *
 * Only shows the product media tagged with the selected color. Media is tagged through its alt text
 * ("01-50003 - Black"), which Liquid turns into a `data-media-color` attribute on every slide and
 * thumbnail (see snippets/media-color.liquid). Products that use color tags show only the media tagged
 * with the selected color (all their media for a color without tagged media); products without any
 * color tags show all their media.
 *
 * The initial state is rendered by Liquid; this script re-applies it whenever the variant changes and
 * keeps the slider's prev/next navigation within the visible media.
 */
(function () {
  'use strict';

  const selectors = {
    productInfo: 'product-info',
    productImages: 'product-images',
    slide: '[data-image-id][data-media-color]',
    thumb: '[data-thumb-item][data-media-color]',
    activeSlide: '[data-image-id].media--active',
    visibleSlide: '[data-image-id]:not(.media--filtered)',
    visibleImageSlide: '[data-image-id][data-type="image"]:not(.media--filtered)',
  };

  const classes = {
    filtered: 'media--filtered',
  };

  const attributes = {
    colorOptionIndex: 'data-color-option-index',
    mediaColor: 'data-media-color',
    mediaId: 'data-media-id',
  };

  function normalize(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  function filterVariantMedia(productInfo, variant) {
    const productImages = productInfo.querySelector(selectors.productImages);
    if (!productImages || !productImages.hasAttribute(attributes.colorOptionIndex)) return;

    const optionIndex = parseInt(productImages.getAttribute(attributes.colorOptionIndex), 10);
    const selectedColor = variant && variant.options ? normalize(variant.options[optionIndex]) : '';
    const slides = [...productImages.querySelectorAll(selectors.slide)];
    const thumbs = [...productInfo.querySelectorAll(selectors.thumb)];
    if (!slides.length) return;

    const usesColorTags = slides.some((slide) => slide.getAttribute(attributes.mediaColor) !== '');
    // A color without any tagged media falls back to showing all the media
    const hasColorMedia = slides.some((slide) => slide.getAttribute(attributes.mediaColor) === selectedColor);

    const isFiltered = (element) => usesColorTags && hasColorMedia && element.getAttribute(attributes.mediaColor) !== selectedColor;

    slides.forEach((slide) => slide.classList.toggle(classes.filtered, isFiltered(slide)));
    thumbs.forEach((thumb) => thumb.classList.toggle(classes.filtered, isFiltered(thumb)));

    // Keep the active media visible, otherwise move to the first media shown for this color (an image if there is one, so videos don't autoplay)
    const activeSlide = productImages.querySelector(selectors.activeSlide);
    if (activeSlide && !activeSlide.classList.contains(classes.filtered)) return;

    const firstVisible = productImages.querySelector(selectors.visibleImageSlide) || productImages.querySelector(selectors.visibleSlide);
    if (!firstVisible) return;

    productImages.dispatchEvent(
      new CustomEvent('theme:media:select', {
        bubbles: true,
        detail: {id: firstVisible.getAttribute(attributes.mediaId)},
      })
    );
  }

  // Prev/next navigation (arrows, swipe, keyboard) should skip filtered media
  customElements.whenDefined('product-images').then(() => {
    const proto = customElements.get('product-images').prototype;

    const getVisibleItems = (productImages) => [...productImages.querySelectorAll(selectors.visibleSlide)];

    proto.getNextMediaId = function () {
      const items = getVisibleItems(this);
      const index = items.indexOf(this.getCurrentMedia());
      const next = items[index + 1] || items[0];

      return next?.getAttribute(attributes.mediaId);
    };

    proto.getPreviousMediaId = function () {
      const items = getVisibleItems(this);
      const index = items.indexOf(this.getCurrentMedia());
      const previous = index > 0 ? items[index - 1] : items[items.length - 1];

      return previous?.getAttribute(attributes.mediaId);
    };
  });

  subscribe(theme.PUB_SUB_EVENTS.variantChange, ({data: {sectionId, variant}}) => {
    const productInfo = document.querySelector(
      `${selectors.productInfo}[data-section="${sectionId}"], ${selectors.productInfo}[data-original-section="${sectionId}"]`
    );

    if (productInfo) filterVariantMedia(productInfo, variant);
  });
})();
